import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiBriefcase,
  FiImage,
  FiSave,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeeAccessSection
  from "../common/EmployeeAccessSection";

import {
  DEFAULT_DEPARTMENTS,
  DEFAULT_DESIGNATIONS,
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  GENDER_OPTIONS,
  INITIAL_EMPLOYEE_FORM,
} from "../utils/employee.constants";

import {
  formatEmployeeCurrency,
  formatEmployeeDateInput,
  formatEmployeeLabel,
  getEmployeeApiError,
  getObjectId,
  getUniqueOptions,
  hasEmployeeLoginAccount,
  isEmployeeLoginActive,
} from "../utils/employee.utils";

import "./EmployeeManagement.css";


const AddEditEmployee = ({
  open,

  employee,

  dropdownData = {},

  onClose,

  onSuccess,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_EMPLOYEE_FORM,
    });


  const [
    errors,
    setErrors,
  ] =
    useState({});


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    profileImage,
    setProfileImage,
  ] =
    useState(null);


  const [
    profilePreview,
    setProfilePreview,
  ] =
    useState("");


  const isEdit =
    Boolean(
      employee?._id
    );


  const existingLoginAccount =
    hasEmployeeLoginAccount(
      employee
    );


  /*
  |--------------------------------------------------------------------------
  | OPTIONS
  |--------------------------------------------------------------------------
  */

  const departments =
    getUniqueOptions(
      dropdownData.departments,
      DEFAULT_DEPARTMENTS
    );


  const designations =
    getUniqueOptions(
      dropdownData.designations,
      DEFAULT_DESIGNATIONS
    );


  const employmentTypes =
    getUniqueOptions(
      dropdownData.employmentTypes,
      EMPLOYMENT_TYPES
    );


  const statuses =
    getUniqueOptions(
      dropdownData.statuses,
      EMPLOYEE_STATUSES
    )
      .filter(
        (
          value
        ) =>
          EMPLOYEE_STATUSES
            .includes(
              String(
                value
              ).toUpperCase()
            )
      );


  const roles =
    Array.isArray(
      dropdownData.roles
    )
      ?
      dropdownData.roles
      :
      [];


  const permissionModules =
    Array.isArray(
      dropdownData.permissionModules
    )
      ?
      dropdownData.permissionModules
      :
      [];


  /*
  |--------------------------------------------------------------------------
  | CURRENT SUBSCRIPTION ASSIGNABLE PERMISSION IDS
  |--------------------------------------------------------------------------
  |
  | dropdownData.permissionModules is already filtered by the backend using
  | the tenant's current subscription modules.
  |
  | Build one source-of-truth Set so:
  |
  | - old permissions from removed/unsubscribed modules are not restored
  | - hidden stale IDs are never submitted during Edit Employee
  |
  |--------------------------------------------------------------------------
  */

  const assignablePermissionIdSet =
    useMemo(
      () => {

        const ids =
          new Set();


        permissionModules
          .forEach(
            (
              module
            ) => {

              (
                Array.isArray(
                  module?.permissions
                )
                  ?
                  module.permissions
                  :
                  []
              )
                .forEach(
                  (
                    permission
                  ) => {

                    const permissionId =
                      getObjectId(
                        permission
                      );


                    if (
                      permissionId
                    ) {

                      ids.add(
                        String(
                          permissionId
                        )
                      );

                    }

                  }
                );

            }
          );


        return ids;

      },
      [
        permissionModules,
      ]
    );


  const sanitizePermissionIds =
    (
      permissionIds
    ) => {

      if (
        !Array.isArray(
          permissionIds
        )
      ) {

        return [];

      }


      return [
        ...new Set(
          permissionIds
            .map(
              getObjectId
            )
            .filter(
              Boolean
            )
            .map(
              String
            )
            .filter(
              (
                permissionId
              ) =>
                assignablePermissionIdSet
                  .has(
                    permissionId
                  )
            )
        ),
      ];

    };


  /*
  |--------------------------------------------------------------------------
  | INITIALIZE
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (
        !open
      ) {

        return;

      }


      if (
        !employee
      ) {

        setForm({
          ...INITIAL_EMPLOYEE_FORM,
        });


        setProfileImage(
          null
        );


        setProfilePreview(
          ""
        );


        setErrors(
          {}
        );


        return;

      }


      const adminUser =
        employee?.userId &&
        typeof employee.userId ===
          "object"
          ?
          employee.userId
          :
          null;


      setForm({

        employeeCode:
          employee?.employeeCode ||
          "",

        name:
          employee?.name ||
          "",

        mobile:
          employee?.mobile ||
          "",

        email:
          employee?.email ||
          "",

        gender:
          employee?.gender ||
          "MALE",

        dateOfBirth:
          formatEmployeeDateInput(
            employee?.dateOfBirth
          ),

        department:
          employee?.department ||
          "",

        designation:
          employee?.designation ||
          "",

        employmentType:
          employee?.employmentType ||
          "FULL_TIME",

        joiningDate:
          formatEmployeeDateInput(
            employee?.joiningDate
          ),

        status:
          employee?.status ||
          "ACTIVE",

        basicSalary:
          employee
            ?.salary
            ?.basicSalary ??
          "",

        allowances:
          employee
            ?.salary
            ?.allowances ??
          "",

        deductions:
          employee
            ?.salary
            ?.deductions ??
          "",

        addressLine:
          employee
            ?.address
            ?.addressLine ||
          "",

        city:
          employee
            ?.address
            ?.city ||
          "",

        state:
          employee
            ?.address
            ?.state ||
          "",

        pincode:
          employee
            ?.address
            ?.pincode ||
          "",

        emergencyName:
          employee
            ?.emergencyContact
            ?.name ||
          "",

        emergencyRelation:
          employee
            ?.emergencyContact
            ?.relation ||
          "",

        emergencyMobile:
          employee
            ?.emergencyContact
            ?.mobile ||
          "",

        loginAccess:
          isEmployeeLoginActive(
            employee
          ),

        roleId:
          getObjectId(
            adminUser?.roleId
          ),

        permissionMode:
          String(
            adminUser?.permissionMode ||
            "ROLE"
          ).toUpperCase(),

        permissionIds:
          sanitizePermissionIds(
            adminUser?.permissions
          ),

        password:
          "",

      });


      setProfilePreview(
        employee?.profileImage ||
        ""
      );


      setProfileImage(
        null
      );


      setErrors(
        {}
      );

    },
    [
      open,
      employee,
      assignablePermissionIdSet,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | BODY SCROLL
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (
        !open
      ) {

        return undefined;

      }


      const previous =
        document
          .body
          .style
          .overflow;


      document
        .body
        .style
        .overflow =
        "hidden";


      return () => {

        document
          .body
          .style
          .overflow =
          previous;

      };

    },
    [
      open,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
    } =
      event.target;


    let nextValue =
      value;


    if (
      [
        "mobile",
        "emergencyMobile",
      ].includes(
        name
      )
    ) {

      nextValue =
        value
          .replace(
            /\D/g,
            ""
          )
          .slice(
            0,
            10
          );

    }


    if (
      name ===
      "pincode"
    ) {

      nextValue =
        value
          .replace(
            /\D/g,
            ""
          )
          .slice(
            0,
            6
          );

    }


    if (
      [
        "basicSalary",
        "allowances",
        "deductions",
      ].includes(
        name
      )
    ) {

      nextValue =
        value.replace(
          /[^\d.]/g,
          ""
        );

    }


    setForm(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          nextValue,
      })
    );


    setErrors(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          "",

        form:
          "",
      })
    );

  };


  /*
  |--------------------------------------------------------------------------
  | IMAGE
  |--------------------------------------------------------------------------
  */

  const handleImage = (
    event
  ) => {

    const file =
      event
        .target
        .files
        ?.[0];


    if (
      !file
    ) {

      return;

    }


    if (
      ![
        "image/jpeg",
        "image/png",
        "image/webp",
      ].includes(
        file.type
      )
    ) {

      setErrors(
        (
          previous
        ) => ({
          ...previous,

          profileImage:
            "Only JPG, PNG or WEBP images are allowed.",
        })
      );

      return;

    }


    if (
      file.size >
      5 *
      1024 *
      1024
    ) {

      setErrors(
        (
          previous
        ) => ({
          ...previous,

          profileImage:
            "Image must be less than 5 MB.",
        })
      );

      return;

    }


    setProfileImage(
      file
    );


    setProfilePreview(
      URL.createObjectURL(
        file
      )
    );

  };


  /*
  |--------------------------------------------------------------------------
  | VALIDATE
  |--------------------------------------------------------------------------
  */

  const validate =
    () => {

      const nextErrors =
        {};


      if (
        !form.name.trim()
      ) {

        nextErrors.name =
          "Employee name is required.";

      }


      if (
        !/^[6-9]\d{9}$/.test(
          form.mobile
        )
      ) {

        nextErrors.mobile =
          "Enter a valid 10-digit mobile number.";

      }


      if (
        form.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          form.email
        )
      ) {

        nextErrors.email =
          "Enter a valid email address.";

      }


      if (
        !form.department
      ) {

        nextErrors.department =
          "Department is required.";

      }


      if (
        !form.designation
      ) {

        nextErrors.designation =
          "Designation is required.";

      }


      if (
        !form.joiningDate
      ) {

        nextErrors.joiningDate =
          "Joining date is required.";

      }


      if (
        form.loginAccess
      ) {

        if (
          !form.roleId
        ) {

          nextErrors.roleId =
            "Role is required.";

        }


        if (
          form.permissionMode ===
            "CUSTOM" &&
          sanitizePermissionIds(
            form.permissionIds
          ).length ===
            0
        ) {

          nextErrors.permissions =
            "Select at least one permission available in the current subscription.";

        }


        if (
          !existingLoginAccount &&
          form.password.length <
            8
        ) {

          nextErrors.password =
            "Password must contain at least 8 characters.";

        }


        if (
          form.password &&
          form.password.length <
            8
        ) {

          nextErrors.password =
            "Password must contain at least 8 characters.";

        }

      }


      setErrors(
        nextErrors
      );


      return (
        Object.keys(
          nextErrors
        ).length ===
        0
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      if (
        !validate()
      ) {

        return;

      }


      try {

        setLoading(
          true
        );


        setErrors(
          {}
        );


        const body =
          new FormData();


        if (
          form.employeeCode
        ) {

          body.append(
            "employeeCode",
            form.employeeCode
          );

        }


        [
          "name",
          "mobile",
          "email",
          "gender",
          "dateOfBirth",
          "department",
          "designation",
          "employmentType",
          "joiningDate",
          "status",
          "roleId",
          "permissionMode",
        ]
          .forEach(
            (
              field
            ) => {

              body.append(
                field,
                form[field] ??
                ""
              );

            }
          );


        body.append(
          "loginAccess",
          String(
            form.loginAccess
          )
        );


        body.append(
          "permissionIds",
          JSON.stringify(
            form.permissionMode ===
              "CUSTOM"
              ?
              sanitizePermissionIds(
                form.permissionIds
              )
              :
              []
          )
        );


        if (
          form.password
        ) {

          body.append(
            "password",
            form.password
          );

        }


        body.append(
          "salary",
          JSON.stringify({
            basicSalary:
              Number(
                form.basicSalary ||
                0
              ),

            allowances:
              Number(
                form.allowances ||
                0
              ),

            deductions:
              Number(
                form.deductions ||
                0
              ),
          })
        );


        body.append(
          "address",
          JSON.stringify({
            addressLine:
              form.addressLine,

            city:
              form.city,

            state:
              form.state,

            pincode:
              form.pincode,
          })
        );


        body.append(
          "emergencyContact",
          JSON.stringify({
            name:
              form.emergencyName,

            relation:
              form.emergencyRelation,

            mobile:
              form.emergencyMobile,
          })
        );


        if (
          profileImage
        ) {

          body.append(
            "profileImage",
            profileImage
          );

        }


        const response =
          isEdit
            ?
            await api.put(
              config.updateEmployee(
                employee._id
              ),
              body
            )
            :
            await api.postFormData(
              config.createEmployee,
              body
            );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to save employee."
          );

        }


        await onSuccess?.(
          response?.data
        );

      } catch (
        requestError
      ) {

        setErrors({
          form:
            getEmployeeApiError(
              requestError,
              "Unable to save employee."
            ),
        });

      } finally {

        setLoading(
          false
        );

      }

    };


  if (
    !open
  ) {

    return null;

  }


  const netSalary =
    Math.max(
      Number(
        form.basicSalary ||
        0
      ) +
      Number(
        form.allowances ||
        0
      ) -
      Number(
        form.deductions ||
        0
      ),
      0
    );


  return ReactDOM
    .createPortal(

      <div className="employee-modal-root">

        <button
          type="button"
          className="employee-modal-backdrop"
          onClick={
            loading
              ?
              undefined
              :
              onClose
          }
          aria-label="Close"
        />


        <section className="employee-modal-dialog">

          <header className="employee-modal-header">

            <div>

              <span className="employee-modal-header-icon">
                <FiBriefcase />
              </span>

              <div>

                <small>
                  EMPLOYEE MANAGEMENT
                </small>

                <h2>
                  {
                    isEdit
                      ?
                      "Edit Employee"
                      :
                      "Add Employee"
                  }
                </h2>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >
              <FiX />
            </button>

          </header>


          <form
            onSubmit={
              handleSubmit
            }
          >

            <div className="employee-modal-body">

              {
                errors.form
                  ?
                  (
                    <div className="employee-modal-form-error">
                      {errors.form}
                    </div>
                  )
                  :
                  null
              }


              <section className="employee-form-section">

                <SectionTitle
                  icon={
                    FiUser
                  }
                  title="Personal Information"
                  description="Basic employee details and contact information."
                />


                <div className="employee-profile-row">

                  <div className="employee-profile-preview">

                    {
                      profilePreview
                        ?
                        (
                          <img
                            src={
                              profilePreview
                            }
                            alt="Employee"
                          />
                        )
                        :
                        (
                          <FiUser />
                        )
                    }

                  </div>


                  <div className="employee-profile-actions">

                    <strong>
                      Profile Image
                    </strong>

                    <span>
                      JPG, PNG or WEBP up to 5 MB
                    </span>


                    <div>

                      <label className="employee-image-button">

                        <FiImage />

                        Choose Image

                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={
                            handleImage
                          }
                        />

                      </label>


                      {
                        profilePreview
                          ?
                          (
                            <button
                              type="button"
                              onClick={
                                () => {

                                  setProfileImage(
                                    null
                                  );

                                  setProfilePreview(
                                    ""
                                  );

                                }
                              }
                            >

                              <FiTrash2 />

                              Remove

                            </button>
                          )
                          :
                          null
                      }

                    </div>

                  </div>

                </div>


                <div className="employee-form-grid">

                  <Field
                    label="Employee Code"
                  >
                    <input
                      name="employeeCode"
                      value={
                        form.employeeCode
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Auto-generated if empty"
                      disabled={
                        isEdit ||
                        loading
                      }
                    />
                  </Field>


                  <Field
                    label="Employee Name *"
                    error={
                      errors.name
                    }
                  >
                    <input
                      name="name"
                      value={
                        form.name
                      }
                      onChange={
                        handleChange
                      }
                    />
                  </Field>


                  <Field
                    label="Mobile Number *"
                    error={
                      errors.mobile
                    }
                  >
                    <input
                      name="mobile"
                      value={
                        form.mobile
                      }
                      onChange={
                        handleChange
                      }
                      maxLength="10"
                    />
                  </Field>


                  <Field
                    label="Email Address"
                    error={
                      errors.email
                    }
                  >
                    <input
                      type="email"
                      name="email"
                      value={
                        form.email
                      }
                      onChange={
                        handleChange
                      }
                    />
                  </Field>


                  <Field label="Gender">

                    <select
                      name="gender"
                      value={
                        form.gender
                      }
                      onChange={
                        handleChange
                      }
                    >

                      {
                        GENDER_OPTIONS.map(
                          (
                            value
                          ) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {
                                formatEmployeeLabel(
                                  value
                                )
                              }
                            </option>
                          )
                        )
                      }

                    </select>

                  </Field>


                  <Field label="Date of Birth">

                    <input
                      type="date"
                      name="dateOfBirth"
                      value={
                        form.dateOfBirth
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>

                </div>

              </section>


              <section className="employee-form-section">

                <SectionTitle
                  icon={
                    FiBriefcase
                  }
                  title="Employment Information"
                  description="Department, designation and employment details."
                />


                <div className="employee-form-grid">

                  <Field
                    label="Department *"
                    error={
                      errors.department
                    }
                  >

                    <select
                      name="department"
                      value={
                        form.department
                      }
                      onChange={
                        handleChange
                      }
                    >

                      <option value="">
                        Select department
                      </option>

                      {
                        departments.map(
                          (
                            value
                          ) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {value}
                            </option>
                          )
                        )
                      }

                    </select>

                  </Field>


                  <Field
                    label="Designation *"
                    error={
                      errors.designation
                    }
                  >

                    <select
                      name="designation"
                      value={
                        form.designation
                      }
                      onChange={
                        handleChange
                      }
                    >

                      <option value="">
                        Select designation
                      </option>

                      {
                        designations.map(
                          (
                            value
                          ) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {value}
                            </option>
                          )
                        )
                      }

                    </select>

                  </Field>


                  <Field label="Employment Type">

                    <select
                      name="employmentType"
                      value={
                        form.employmentType
                      }
                      onChange={
                        handleChange
                      }
                    >

                      {
                        employmentTypes.map(
                          (
                            value
                          ) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {
                                formatEmployeeLabel(
                                  value
                                )
                              }
                            </option>
                          )
                        )
                      }

                    </select>

                  </Field>


                  <Field
                    label="Joining Date *"
                    error={
                      errors.joiningDate
                    }
                  >

                    <input
                      type="date"
                      name="joiningDate"
                      value={
                        form.joiningDate
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Status">

                    <select
                      name="status"
                      value={
                        form.status
                      }
                      onChange={
                        handleChange
                      }
                    >

                      {
                        statuses.map(
                          (
                            value
                          ) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {
                                formatEmployeeLabel(
                                  value
                                )
                              }
                            </option>
                          )
                        )
                      }

                    </select>

                  </Field>

                </div>

              </section>


              <section className="employee-form-section">

                <SectionTitle
                  title="Current Salary Structure"
                  description="This configuration is used as the base for monthly payroll."
                />


                <div className="employee-form-grid employee-salary-form-grid">

                  <Field label="Basic Salary">

                    <input
                      name="basicSalary"
                      value={
                        form.basicSalary
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Allowances">

                    <input
                      name="allowances"
                      value={
                        form.allowances
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Fixed Deductions">

                    <input
                      name="deductions"
                      value={
                        form.deductions
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <div className="employee-net-salary">

                    <span>
                      Net Salary
                    </span>

                    <strong>
                      {
                        formatEmployeeCurrency(
                          netSalary
                        )
                      }
                    </strong>

                  </div>

                </div>

              </section>


              <section className="employee-form-section">

                <SectionTitle
                  title="Address & Emergency Contact"
                  description="Optional address and emergency information."
                />


                <div className="employee-form-grid">

                  <Field
                    label="Address"
                    full
                  >

                    <textarea
                      name="addressLine"
                      value={
                        form.addressLine
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="City">

                    <input
                      name="city"
                      value={
                        form.city
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="State">

                    <input
                      name="state"
                      value={
                        form.state
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Pincode">

                    <input
                      name="pincode"
                      value={
                        form.pincode
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Emergency Name">

                    <input
                      name="emergencyName"
                      value={
                        form.emergencyName
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Relation">

                    <input
                      name="emergencyRelation"
                      value={
                        form.emergencyRelation
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>


                  <Field label="Emergency Mobile">

                    <input
                      name="emergencyMobile"
                      value={
                        form.emergencyMobile
                      }
                      onChange={
                        handleChange
                      }
                    />

                  </Field>

                </div>

              </section>


              <EmployeeAccessSection
                form={form}
                setForm={setForm}
                roles={roles}
                permissionModules={
                  permissionModules
                }
                errors={errors}
                setErrors={setErrors}
                loading={loading}
                existingLoginAccount={
                  existingLoginAccount
                }
              />

            </div>


            <footer className="employee-modal-footer">

              <button
                type="button"
                className="employee-modal-cancel"
                onClick={
                  onClose
                }
                disabled={
                  loading
                }
              >
                Cancel
              </button>


              <button
                type="submit"
                className="employee-modal-save"
                disabled={
                  loading
                }
              >

                <FiSave />

                {
                  loading
                    ?
                    "Please wait..."
                    :
                    isEdit
                      ?
                      "Update Employee"
                      :
                      "Create Employee"
                }

              </button>

            </footer>

          </form>

        </section>

      </div>,

      document.body

    );

};


const SectionTitle = ({
  icon: Icon,
  title,
  description,
}) => (

  <div className="employee-form-section-heading">

    {
      Icon
        ?
        (
          <span>
            <Icon />
          </span>
        )
        :
        null
    }

    <div>

      <h3>
        {title}
      </h3>

      <p>
        {description}
      </p>

    </div>

  </div>

);


const Field = ({
  label,
  error,
  full,
  children,
}) => (

  <div
    className={
      full
        ?
        "employee-form-field employee-form-full"
        :
        "employee-form-field"
    }
  >

    <label>
      {label}
    </label>

    {children}

    {
      error
        ?
        (
          <small className="employee-field-error">
            {error}
          </small>
        )
        :
        null
    }

  </div>

);


export default AddEditEmployee;